import axios, { AxiosError, AxiosInstance } from 'axios';

import { ADMIN_LOGIN_PATH, LOGIN_PATH } from '../basePath';

declare global {
  interface Window {
    APP_CONFIG?: { API_URL?: string };
  }
}

export const API_BASE_URL =
  window.APP_CONFIG?.API_URL || import.meta.env.VITE_API_URL || 'http://localhost:8010/api';

export const TOKEN_KEY = 'zpos_token';
export const SESSION_KEY = 'zpos_session';
export const ADMIN_TOKEN_KEY = 'zpos_admin_token';
/** Why the session ended, shown on the sign-in screen it lands on. */
export const LOCKED_REASON_KEY = 'zpos_locked_reason';

function make(tokenKey: string, onUnauthorised: () => void): AxiosInstance {
  const inst = axios.create({ baseURL: API_BASE_URL, headers: { 'Content-Type': 'application/json' } });
  inst.interceptors.request.use((config) => {
    const token = localStorage.getItem(tokenKey);
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  inst.interceptors.response.use(
    (r) => r,
    (error: AxiosError<{ detail?: unknown }>) => {
      const status = error.response?.status;
      const url = error.config?.url || '';
      const isLogin = url.includes('/auth/login') || url.includes('/auth/admin/login');
      if (status === 401 && !isLogin && localStorage.getItem(tokenKey)) {
        onUnauthorised();
      }
      return Promise.reject(error);
    },
  );
  return inst;
}

export function clearShopSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(SESSION_KEY);
}

const api = make(TOKEN_KEY, () => {
  clearShopSession();
  window.location.href = LOGIN_PATH;
});

// A lapsed subscription (402) ends the session; the login page explains it.
api.interceptors.response.use(
  (r) => r,
  (error: AxiosError<{ detail?: unknown }>) => {
    if (error.response?.status === 402 && !(error.config?.url || '').includes('/auth/login')) {
      localStorage.setItem(LOCKED_REASON_KEY, errorMessage(error));
      clearShopSession();
      window.location.href = LOGIN_PATH;
    }
    return Promise.reject(error);
  },
);

export const adminApi = make(ADMIN_TOKEN_KEY, () => {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  window.location.href = ADMIN_LOGIN_PATH;
});

/** A readable message from any API error (FastAPI detail: string | {message} | validation list). */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const e = err as AxiosError<{ detail?: unknown }>;
  if (!e?.response) return e?.message === 'Network Error' ? 'Cannot reach the server. Check the network.' : fallback;
  const d = e.response.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((x: { loc?: unknown[]; msg?: string }) => `${(x.loc || []).slice(-1)[0]}: ${x.msg}`).join('; ');
  }
  if (d && typeof d === 'object' && 'message' in d) return String((d as { message: unknown }).message);
  return fallback;
}

export function errorDetail<T = Record<string, unknown>>(err: unknown): T | null {
  const d = (err as AxiosError<{ detail?: unknown }>)?.response?.data?.detail;
  return d && typeof d === 'object' && !Array.isArray(d) ? (d as T) : null;
}

/** ws(s):// URL for the live-updates socket, derived from the API base. */
export function wsUrl(token: string): string {
  const base = new URL(API_BASE_URL, window.location.href);
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${base.toString().replace(/\/$/, '')}/ws?token=${encodeURIComponent(token)}`;
}

export default api;
